import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import StandardSelect from "../../../../shared/formComponents/StandardSelect/StandardSelect";
import { QokrAvatar } from "../QokrPrimitives/QokrPrimitives";
import "./_qokr-people-picker.scss";

// ═══════════════════════════════════════════════════════════════════════════
// Owner + contributors, picked from the interview roster (`qokr_roster`):
// active members only (§5.2). The owner can never also be a contributor.
//
// Owner is the Annual OKR card's bordered `StandardSelect`. Contributors are
// chips plus a checklist menu — `StandardSelect` has no multi mode, and the
// CEO needs to SEE who is on an objective at a glance.
// ═══════════════════════════════════════════════════════════════════════════

const MENU_WIDTH = 300;
const MENU_GAP = 6;
const VIEWPORT_PAD = 12;

// ⚠️ PORTALLED TO <body> AND POSITIONED `fixed` against its button. Inline,
// it sat inside the chat's scrolling list and widened it near the right edge
// (horizontal scroll, card shifting sideways). It flips to stay on screen.
const ContributorMenu = ({
  anchorRef,
  options,
  selected,
  onToggle,
  onClose,
  emptyText,
}) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const height = ref.current ? ref.current.offsetHeight : 0;
      const left = Math.max(
        VIEWPORT_PAD,
        Math.min(rect.left, window.innerWidth - MENU_WIDTH - VIEWPORT_PAD),
      );
      const below = rect.bottom + MENU_GAP;
      const top =
        below + height > window.innerHeight - VIEWPORT_PAD
          ? Math.max(VIEWPORT_PAD, rect.top - MENU_GAP - height)
          : below;
      setPos({ top, left });
    };
    place();
    // Capture: the scroll happens on the message list, not the window.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchorRef]);

  // Each tick applies immediately, so closing can never lose work.
  useEffect(() => {
    const onDown = (event) => {
      if (ref.current?.contains(event.target)) return;
      if (anchorRef.current?.contains(event.target)) return;
      onClose();
    };
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  return ReactDOM.createPortal(
    <div
      className="qokr-contrib-menu"
      ref={ref}
      role="menu"
      style={{
        position: "fixed",
        width: MENU_WIDTH,
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        // Hidden for the one frame before it is measured and placed.
        visibility: pos ? "visible" : "hidden",
      }}
    >
      <div className="qokr-contrib-menu__head">
        <span>Contributors</span>
        <span>{selected.length} selected</span>
      </div>
      {/* ⚠️ AN EMPTY STATE, NOT AN EMPTY BOX. With nobody to offer — a company
          whose only active member is the owner / this person, or a roster
          that hasn't arrived — the menu used to render its header over
          nothing, which read as broken. */}
      {!options.length ? (
        <div className="qokr-contrib-menu__empty">
          <span className="qokr-contrib-menu__empty-icon">
            <i className="fa-solid fa-user-group" aria-hidden="true" />
          </span>
          <p className="qokr-contrib-menu__empty-title">No one to add yet</p>
          <p className="qokr-contrib-menu__empty-text">{emptyText}</p>
        </div>
      ) : null}
      <div className="qokr-contrib-menu__list">
        {options.map((member) => {
          const checked = selected.includes(String(member.user_id));
          return (
            <button
              key={member.user_id}
              type="button"
              role="menuitemcheckbox"
              aria-checked={checked}
              className={`qokr-contrib-menu__item${checked ? " is-checked" : ""}`}
              onClick={() => onToggle(String(member.user_id))}
            >
              <span className="qokr-contrib-menu__check">
                {checked ? (
                  <i className="fa-solid fa-check" aria-hidden="true" />
                ) : null}
              </span>
              <QokrAvatar person={member} size="xs" />
              <span className="qokr-contrib-menu__name">{member.name}</span>
              <span className="qokr-contrib-menu__role">{member.role}</span>
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
};

// `members` = roster members; `value` = owner user_id.
export const OwnerSelect = ({ members = [], value, onChange, disabled }) => (
  <div className="odc__owner-field">
    <StandardSelect
      variant="bordered"
      className="odc__owner-select"
      options={members.map((member) => ({
        value: member.user_id,
        label: member.name,
      }))}
      value={value}
      onChange={(option) => onChange(option?.value ?? null)}
      isSearchable={false}
      placeholder="Choose an owner"
      isDisabled={disabled}
      maxMenuHeight={220}
    />
  </div>
);

// `value` = contributor user_ids; `excludeIds` = people who can't contribute
// here (the owner; on an individual card, the member themselves).
export const ContributorChips = ({
  members = [],
  value = [],
  excludeIds = [],
  onChange,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const addRef = useRef(null);
  const excluded = excludeIds.map(String);
  const options = members.filter(
    (member) => !excluded.includes(String(member.user_id)),
  );
  // BE ids are strings (§0); normalise so `includes` never misses on a type.
  const selected = value.map(String).filter((id) => !excluded.includes(id));
  const nameOf = (id) =>
    members.find((member) => String(member.user_id) === String(id))?.name ||
    "Former member";

  const toggle = (id) =>
    onChange(
      selected.includes(id)
        ? selected.filter((selectedId) => selectedId !== id)
        : [...selected, id],
    );

  return (
    <div className="qokr-contribs">
      {selected.map((id) => (
        <span key={id} className="qokr-contrib-chip">
          <QokrAvatar person={{ name: nameOf(id) }} size="xs" />
          {nameOf(id)}
          <button
            type="button"
            className="qokr-contrib-chip__x"
            onClick={() => toggle(id)}
            aria-label={`Remove ${nameOf(id)}`}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </span>
      ))}
      <span className="qokr-contribs__add-wrap">
        <button
          type="button"
          className="qokr-contribs__add"
          ref={addRef}
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
        >
          <i className="fa-solid fa-plus" aria-hidden="true" />
          {selected.length ? "Edit" : "Add contributors"}
        </button>
        {menuOpen ? (
          <ContributorMenu
            anchorRef={addRef}
            options={options}
            selected={selected}
            onToggle={toggle}
            onClose={() => setMenuOpen(false)}
            emptyText={
              members.length
                ? "Add team members to choose contributors."
                : "Your team list hasn't loaded yet. Try again in a moment."
            }
          />
        ) : null}
      </span>
    </div>
  );
};
